// src/components/Sidebar/SidebarDailyCheckin.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    ListItemButton,
    ListItemIcon,
    CircularProgress,
    Typography,
    Box,
    Chip,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { getCheckinStatus, postCheckin } from '../../api/points';
import { useUser } from '../../contexts/UserContext';

dayjs.extend(duration);

const formatCountdown = (ms) => {
    if (ms <= 0) return '--:--:--';
    const d = dayjs.duration(ms);
    return [
        String(d.hours()).padStart(2, '0'),
        String(d.minutes()).padStart(2, '0'),
        String(d.seconds()).padStart(2, '0'),
    ].join(':');
};

// ✅ 한 줄 요약 텍스트 (Ready 상태는 한 줄 유지)
const oneLine = {
    fontSize: 12,
    color: 'text.secondary',
    lineHeight: 1.2,
    mt: 0.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

// ✅ 완료 상태(주간/월간 2줄)는 "절대 ... 안 뜨게" (ellipsis 제거)
const twoLine = {
    fontSize: 12,
    color: 'text.secondary',
    lineHeight: 1.2,
    mt: 0.2,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    display: 'inline-block',
    minWidth: 0,
};

export default function SidebarDailyCheckin({ onItemClick }) {
    const { refreshUserInfo } = useUser();

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [countdown, setCountdown] = useState('');

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await getCheckinStatus();
            setStatus(data);
        } catch (e) {
            console.error('출석 상태 로딩 실패', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // ⏱ 초기화 카운트다운 (출석 완료 시만)
    useEffect(() => {
        if (!status?.checked_today || !status?.next_reset_at_kst) return;

        const timer = setInterval(() => {
            const diff = dayjs(status.next_reset_at_kst).diff(dayjs());
            if (diff <= 0) {
                setCountdown('초기화!');
                fetchStatus();
            } else {
                setCountdown(formatCountdown(diff));
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [status?.checked_today, status?.next_reset_at_kst, fetchStatus]);

    const handleCheckin = async (e) => {
        e.stopPropagation();
        setSubmitting(true);
        try {
            await postCheckin();
            await refreshUserInfo();
            await fetchStatus();
            onItemClick?.();
        } catch (e) {
            alert(e?.response?.data?.detail || '출석체크 실패');
        } finally {
            setSubmitting(false);
        }
    };

    // ── 계산
    const consecutiveDays = status?.consecutive_days ?? 0;
    const weekRemainder =
        consecutiveDays === 0 ? 0 : (consecutiveDays % 7 === 0 ? 7 : consecutiveDays % 7);

    const monthChecked = status?.current_month_checked_days ?? 0;
    const monthTotal = status?.current_month_total_days ?? 30;

    // ✅ Ready 문구 (한 줄)
    const secondaryReady =
        weekRemainder === 6
            ? '매일 1회 · 내일 주간보너스 +50'
            : `매일 1회 · 주간 ${weekRemainder}/7`;

    // ✅ 제목 + 칩 (좁으면 칩만 아래로)
    const PrimaryRow = ({ title, chipLabel, titleColor = 'text.primary' }) => (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 1,
                rowGap: 0.5,
                minWidth: 0,
            }}
        >
            <Typography
                sx={{
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.1,
                    flexBasis: '100%',
                    color: titleColor,
                }}
            >
                {title}
            </Typography>

            <Chip
                size="small"
                label={chipLabel}
                variant="outlined"
                color="primary"
                sx={{
                    height: 20,
                    fontWeight: 700,
                    flexShrink: 0,
                    fontFamily: title === '출석 완료' ? 'monospace' : 'inherit',
                }}
            />
        </Box>
    );

    // ✅ 리스트 항목처럼 보이게: Layout은 Grid로
    const commonSx = {
        alignItems: 'flex-start',
        display: 'grid',
        gridTemplateColumns: '28px 1fr',
        columnGap: 1,
    };

    if (loading) {
        return (
            <ListItemButton disabled sx={commonSx}>
                <ListItemIcon sx={{ minWidth: 0, mt: 0.5 }}>
                    <CircularProgress size={18} />
                </ListItemIcon>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>출석 로딩중...</Typography>
                </Box>
            </ListItemButton>
        );
    }

    if (status?.checked_today) {
        return (
            <ListItemButton disabled sx={commonSx}>
                <ListItemIcon sx={{ minWidth: 0, mt: 0.5 }}>
                    <EventBusyIcon color="success" />
                </ListItemIcon>

                <Box sx={{ minWidth: 0 }}>
                    <PrimaryRow title="출석 완료" chipLabel={countdown} />

                    {/* ✅ 주간 / 월간 2줄 + ... 방지 */}
                    <Box sx={{ mt: 0.2 }}>
                        <Typography sx={{ ...twoLine, mt: 0 }}>{`주간 ${weekRemainder}/7`}</Typography>
                        <Typography sx={twoLine}>{`월간 ${monthChecked}/${monthTotal}`}</Typography>
                    </Box>
                </Box>
            </ListItemButton>
        );
    }

    return (
        <ListItemButton onClick={handleCheckin} disabled={submitting} sx={commonSx}>
            <ListItemIcon sx={{ minWidth: 0, mt: 0.5 }}>
                {submitting ? <CircularProgress size={18} /> : <EventAvailableIcon color="primary" />}
            </ListItemIcon>

            <Box sx={{ minWidth: 0 }}>
                <PrimaryRow title="출석체크" chipLabel="+10 Leaf" titleColor="primary.main" />
                <Typography sx={oneLine}>{secondaryReady}</Typography>
            </Box>
        </ListItemButton>
    );
}
